import { useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { gsap, useGSAP } from '@/lib/gsap-config';

/** A bounded, procedural image sequence shared by the ideation and type workbenches. */
export function StudioSequence({ variant }: { variant: 'flow' | 'type' }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();
  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const surface = canvas.current;
      const ctx = surface?.getContext('2d');
      if (!surface || !ctx) return;
      const frames = Array.from({ length: 28 }, (_, index) => {
        const image = document.createElement('canvas');
        image.width = 480; image.height = 240;
        const pen = image.getContext('2d')!;
        const progress = index / 27;
        pen.strokeStyle = theme === 'light' ? '#316c9160' : '#8bbddc60';
        pen.lineWidth = 0.7;
        for (let line = 0; line < 12; line++) {
          pen.beginPath();
          if (variant === 'flow') {
            const y = 20 + line * 18;
            pen.moveTo(0, y);
            pen.bezierCurveTo(140, y, 230, 120 + Math.sin(progress * Math.PI) * 35, 480, y);
          } else {
            const width = 35 + line * 15 + progress * 25;
            pen.ellipse(240, 120, width, 95, progress * 0.4, 0, Math.PI * 2);
          }
          pen.stroke();
        }
        return image;
      });
      let previous = -1;
      const playhead = { frame: 0 };
      const paint = () => {
        const index = Math.round(playhead.frame);
        if (index === previous) return;
        previous = index;
        ctx.clearRect(0, 0, 480, 240);
        ctx.drawImage(frames[index], 0, 0);
      };
      paint();
      gsap.to(playhead, { frame: 27, ease: 'none', onUpdate: paint, scrollTrigger: {
        trigger: surface.parentElement, start: 'top 85%', end: 'bottom 15%', scrub: 0.6,
      } });
      return () => {
        ctx.clearRect(0, 0, 480, 240);
        frames.forEach((frame) => { frame.width = 0; frame.height = 0; });
      };
    });
    return () => media.revert();
  }, { scope: canvas, dependencies: [theme, variant], revertOnUpdate: true });
  return <canvas className="studio-sequence" ref={canvas} width={480} height={240} aria-hidden="true" />;
}
